/**
 * Setlist initial-state fetch.
 *
 * # This is a PRIME, not a poller, and the distinction is the operator's
 *
 * There is deliberately no interval here. The operator's constraint, quoted
 * in the ask that produced this feature, is that resource usage matters
 * *"only when it affects live performance"* - and a polled endpoint costs
 * most exactly when it matters least to be costing anything. So live updates
 * ride the `setlist_update` push (change-driven, zero idle cost), and this
 * file exists only to answer the one question a push cannot: **what was
 * already true before we connected.**
 *
 * OPBridge does not emit a frame on connect, and that is deliberate on their
 * side too - a synthetic opening frame would report `current: null` to a
 * receiver that has simply not been told anything yet, which is the same lie
 * in the other direction and would arrive looking authoritative.
 *
 * So: prime once per connection, then listen.
 */

import { createEffect, on } from 'solid-js';
import { getConnection, primaryBridge, connectionsVersion } from '../opbridge/store.ts';
import { consumerWsConnected } from '../opbridge/consumerWs.ts';
import { OpbridgeError } from '../opbridge/httpClient.ts';
import { recordSetlistState, recordSetlistFailure } from './state.ts';

/**
 * Read `GET /setlist/state` from the bridge that owns the setlist and record
 * it. Never throws.
 *
 * **Which bridge:** the PRIMARY one, not a fan-out. OPSetlist pushes a song
 * change to one OPBridge, and that daemon is what holds `current` and
 * `last_sent`. Asking several would mean several answers to a question with
 * one truth, and the last one to reply would win - a race with no
 * tie-breaker. If the rig ever runs setlists on a non-default bridge this
 * becomes a real choice and should be surfaced, not guessed.
 */
export async function primeSetlistState(): Promise<void> {
  const cfg = primaryBridge();
  if (!cfg) return;
  const conn = getConnection(cfg.id);
  if (!conn || conn.status() !== 'connected') return;
  const primeIssuedAt = Date.now();
  try {
    recordSetlistState(await conn.httpClient().setlistState(), { primeIssuedAt });
  } catch (err) {
    const status = err instanceof OpbridgeError ? err.httpStatus : undefined;
    const msg = err instanceof OpbridgeError ? err.message
              : err instanceof Error           ? err.message
              : String(err);
    if (status === 401) {
      // `opbuttons-full` gained the literal `/setlist/state` at v17, and
      // OPBridge measured the reconcile carrying it onto already-issued
      // bearers with no re-pair. A 401 here therefore means a token that is
      // NOT bundle-backed - which is a re-pair, not an app bug, and the
      // operator cannot guess that from a dark tile.
      recordSetlistFailure('unauthorized',
        'This pairing has no /setlist/state scope. Re-pair this computer to re-mint its token.');
    } else if (status === 404) {
      recordSetlistFailure('error', 'This OPBridge is too old to have /setlist/state (needs v1.15.131).');
    } else {
      recordSetlistFailure('error', msg);
    }
  }
}

/**
 * Prime on first connect and on any reconnect. Idempotent; call once at boot.
 *
 * ⚠️ **`connectionsVersion()` is read explicitly and is LOAD-BEARING.**
 * `getConnection` is a plain `Map.get` and tracks nothing, so an effect
 * depending on `primaryBridge()` alone would never re-run when a connection
 * object appears or is swapped. That exact omission has silently disabled
 * whole modules in this codebase before - `pedals/rebind.ts` carries the
 * same note for the same reason.
 *
 * **Re-primes on RECONNECT, not just first connect.** Anything could have
 * changed while the socket was down and the push is silent about the gap, so
 * coming back without re-reading would leave the tiles confidently showing
 * pre-disconnect values - which is the original bug wearing a different hat.
 */
export function installSetlistPrime(): void {
  createEffect(on(
    () => {
      const v = connectionsVersion();
      const cfg = primaryBridge();
      if (!cfg) return `none:${v}`;
      // The push socket coming back is a reconnect too: an OPBridge restart
      // faster than the health check's three misses never changes the HTTP
      // status, so only the socket's reconnect says the frames were missed.
      return `${cfg.id}:${getConnection(cfg.id)?.status() ?? 'gone'}:${v}:ws=${consumerWsConnected()}`;
    },
    (key) => {
      if (!key.includes(':connected:')) return;
      void primeSetlistState();
    },
  ));
}
